// frontend/src/hooks/useMlModels.ts

import { useState, useEffect, useCallback } from 'react';
import { MlModelsService } from '../services/mlModels.service';

export const useMlModels = () => {
  const [models, setModels] = useState<{
    alias: string;
    path: string;
    dataset_name: string;
    pipeline_version: string;
    experiment_id: string;
    model_name: string;
  }[]>([]);

  const [selectedAlias, setSelectedAlias] = useState<string | null>(null);

  const [schema, setSchema] = useState<{
    alias: string;
    path: string;
    dataset_name: string;
    pipeline_version: string;
    experiment_id: string;
    model_name: string;
    feature_schema: { features: string[] };
    target_name: string;
    problem_type: string;
    model_metadata: Record<string, any>;
  } | null>(null);

  const [prediction, setPrediction] = useState<{
    model_metadata: Record<string, any>;
    predictions: number[];
  } | null>(null);

  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============================================================
  // Lista de modelos
  // ============================================================
  const loadModels = useCallback(async () => {
    try {
      setLoadingModels(true);
      setError(null);
      const data = await MlModelsService.listModels();
      setModels(data);
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || 'Erro ao listar modelos'
      );
    } finally {
      setLoadingModels(false);
    }
  }, []);

  // ============================================================
  // Schema do modelo escolhido
  // ============================================================
  const loadSchema = useCallback(async (alias: string) => {
    if (!alias) return;
    try {
      setLoadingSchema(true);
      setError(null);
      setPrediction(null);
      const data = await MlModelsService.getModelSchema(alias);
      setSchema(data);
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || 'Erro ao carregar schema'
      );
      setSchema(null);
    } finally {
      setLoadingSchema(false);
    }
  }, []);

  // ============================================================
  // Selecionar modelo
  // ============================================================
  const selectModel = useCallback((alias: string) => {
    setSelectedAlias(alias);
  }, []);

  // ============================================================
  // Executar predição
  // ============================================================
  const runPrediction = useCallback(
    async (values: Record<string, number>) => {
      if (!schema) {
        setError('Nenhum modelo carregado');
        return;
      }
      try {
        setPredicting(true);
        setError(null);
        const data = await MlModelsService.predict({
          model_path: `${schema.path}/model_bundle.pkl`,
          data: [values],
        });
        setPrediction(data);
      } catch (err: any) {
        setError(
          err.response?.data?.error || err.message || 'Erro ao executar predição'
        );
      } finally {
        setPredicting(false);
      }
    },
    [schema]
  );

  // ============================================================
  // Efeitos
  // ============================================================

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  useEffect(() => {
    if (selectedAlias) {
      loadSchema(selectedAlias);
    } else {
      setSchema(null);
      setPrediction(null);
    }
  }, [selectedAlias, loadSchema]);

  return {
    models,
    selectedAlias,
    schema,
    prediction,
    loadingModels,
    loadingSchema,
    predicting,
    error,
    selectModel,
    runPrediction,
    reload: loadModels,
  };
};